// Builds the Business Loan / GP Loan Account Statement from a loan summary
// (generateLoanSummaryForMember). The screen and the PDF both draw from this one
// object, so they always show the same figures in the same order.
import {
  formatISODateToLocalDMY,
  getDisbursementAmount,
  maskAadharNumber,
  getFutureEmiAmountWithMonths,
  getStatusOfMember,
  getCurrentDateTimeWithOffset,
} from "./utils";
import { buildStatementPaymentRows } from "./statementRows";

// Where the backend attaches the foreclosure_approval row (attachForeclosureWaivers.js).
const FORECLOSURE_APPROVAL_KEY = "fk_member_details_hasOne_member_foreclosure_approval_memberId";

export const STATEMENT_PRODUCTS = {
  BL: { label: "Business Loan", deductsCharges: true },
  GPL: { label: "GP Loan", deductsCharges: false },
};

// Whole rupees with Indian digit grouping; null/undefined -> "-".
export const formatInr = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? Math.round(n).toLocaleString("en-IN") : String(value);
};

const joinAddress = (s) =>
  [s.permanentDoorNo, s.permanentStreetNo, s.permanentPanchayat, s.permanentTaluk, s.permanentDistrict]
    .filter((part) => part !== undefined && part !== null && String(part).trim() !== "")
    .join(", ") + (s.permanentPincode ? ` - ${s.permanentPincode}` : "");

const futureInstalments = (summary) => {
  const [amount, count] = String(getFutureEmiAmountWithMonths(summary)).split("/");
  return Number(count) > 0 ? `Rs.${formatInr(amount)} (${count} instalments)` : "None";
};

export const buildAccountStatement = (summary, { product = "BL", branchName = "" } = {}) => {
  if (!summary) return null;
  const config = STATEMENT_PRODUCTS[product] || STATEMENT_PRODUCTS.BL;
  const sanctionDate = formatISODateToLocalDMY(summary.branchManagerStatusUpdatedAt);
  const tenure = summary.proposedLoanDetails?.tenureInMonths;
  const totalEmiPaid = summary.totalEmiPaid || 0;
  const deductions = config.deductsCharges
    ? (summary.insuranceAmount || 0) + (summary.processingCharge || 0) + (summary.gst || 0) + (summary.securityDeposit || 0)
    : 0;

  const charges = [
    ["Loan Amount Disbursed", getDisbursementAmount(summary)],
    ["Life Insurance Premium", summary.insuranceAmount || 0],
    ["Processing Fee", summary.processingCharge || 0],
    ["GST", summary.gst || 0],
    ["Security Deposit", summary.securityDeposit || 0],
    [`Total Service Interest for ${tenure} Months`, summary.totalLoanInterest || 0],
  ];
  const debt = charges.reduce((sum, [, amount]) => sum + Number(amount || 0), 0);

  const rows = [];
  let balance = 0;
  const push = (row) => {
    balance += Number(row.increasedBy || 0) - Number(row.decreasedBy || 0);
    rows.push({ interest: null, principal: null, note: null, noteTone: null, ...row, balance });
  };

  charges.forEach(([description, amount], index) =>
    push({ key: `charge-${index}`, kind: "charge", group: "charges", collectionDate: sanctionDate, scheduledDate: null, description, increasedBy: Number(amount || 0), decreasedBy: null })
  );
  if (config.deductsCharges) {
    push({ key: "deduction", kind: "deduction", group: "charges", collectionDate: sanctionDate, scheduledDate: null, description: "Deducted at disbursement: processing fee & other charges", increasedBy: null, decreasedBy: deductions });
  }

  buildStatementPaymentRows(summary.schedule, formatISODateToLocalDMY, { isSettled: summary.isSettled })
    // On a closed loan the unpaid instalments were settled by the closure, not missed;
    // the settlement row below covers them.
    .filter((row) => !(summary.isSettled && row.kind === "due"))
    .forEach((row) =>
      push({
        key: row.key,
        kind: row.kind,
        group: "repayments",
        collectionDate: row.collectionDate ? formatISODateToLocalDMY(row.collectionDate) : null,
        scheduledDate: row.scheduledDate ? formatISODateToLocalDMY(row.scheduledDate) : null,
        description: row.description,
        note: row.note,
        noteTone: row.kind === "due" ? "danger" : row.note && row.note.includes("still due") ? "warning" : "muted",
        increasedBy: null,
        decreasedBy: row.decreasedBy,
        interest: row.interest,
        principal: row.principal,
      })
    );

  // A foreclosure is shown as one highlighted row with the amount received and no
  // running balance - the statement does not try to tally it. How the amount was
  // worked out is listed separately (foreclosureSettlement) for reference.
  const approval = summary[FORECLOSURE_APPROVAL_KEY];
  let foreclosureSettlement = null;
  if (summary.isSettled && approval) {
    const closeDate = formatISODateToLocalDMY(summary.lastPaidDate || new Date());
    const paid = approval.collectedAmount != null ? Math.round(Number(approval.collectedAmount) || 0) : null;
    rows.push({
      key: "foreclosure-payment",
      kind: "foreclosure",
      group: "repayments",
      collectionDate: closeDate,
      scheduledDate: null,
      description: "Foreclosure amount received",
      note: "Settlement details below",
      noteTone: "muted",
      increasedBy: null,
      decreasedBy: paid,
      interest: null,
      principal: null,
      balance: null,
    });

    const rupees = (value) => `Rs.${formatInr(value)}`;
    const total = Number(approval.totalPayableAmount) || 0;
    const depositRecorded = approval.isSecurityDepositAdjusted != null;
    const depositUsed = approval.isSecurityDepositAdjusted ? Number(approval.securityDepositAmount) || 0 : 0;
    const waiver = Number(approval.waiverAmount) || 0;
    const rate = Number(approval.forecloseChargesPercentage);
    foreclosureSettlement = [
      { label: "Outstanding Principal", value: rupees(approval.totalOutstandingAmount) },
      { label: `Foreclosure Charges${Number.isFinite(rate) ? ` (${rate}%)` : ""}`, value: rupees(approval.forecloseChargesAmount) },
      { label: "GST on Charges (18%)", value: rupees(approval.forecloseGstAmount) },
      { label: "Total Payable", value: rupees(total), tone: "total" },
      {
        label: depositRecorded
          ? `Security Deposit (${approval.isSecurityDepositAdjusted ? "returned, deducted" : "not returned"})`
          : "Security Deposit",
        value: !depositRecorded ? "-" : approval.isSecurityDepositAdjusted ? `- ${rupees(depositUsed)}` : rupees(approval.securityDepositAmount),
      },
      { label: "Net Payable", value: rupees(Math.max(0, total - depositUsed)), tone: "total" },
      { label: "Waiver", value: waiver > 0 ? rupees(waiver) : "-", tone: waiver > 0 ? "waiver" : null },
      { label: "Amount Paid", value: paid != null ? rupees(paid) : "-" },
    ];
  }

  const remaining = summary.isSettled ? 0 : Math.round(debt - totalEmiPaid - deductions);
  const overdue = Math.round((summary.overduePrincipal || 0) + (summary.overdueInterest || 0));
  const loanItems = [
    { label: "Loan Amount", value: `Rs.${formatInr(getDisbursementAmount(summary))}` },
    { label: "Interest Rate", value: `${summary.proposedLoanDetails?.rateOfInterest}% (Fixed)` },
    { label: "Tenure", value: `${tenure} months, monthly EMIs` },
    { label: "EMI Amount", value: `Rs.${formatInr(summary.emiAmount)}` },
    { label: "Instalment Period", value: summary.firstEmiDate && summary.lastEmiDate ? `${formatISODateToLocalDMY(summary.firstEmiDate)} to ${formatISODateToLocalDMY(summary.lastEmiDate)}` : "-" },
    { label: "Sanction Date", value: sanctionDate },
  ];
  if (product === "GPL") {
    loanItems.push({ label: "Ornament Weight", value: summary.ornamentWeightByBm ? `${summary.ornamentWeightByBm} g` : "N/A" });
    loanItems.push({ label: "GST Percentage", value: summary.gstByBm ? `${summary.gstByBm}%` : "N/A" });
  }

  return {
    product,
    productLabel: config.label,
    generatedOn: getCurrentDateTimeWithOffset(),
    customerName: String(summary.memberName || "").trim(),
    agreementNo: summary.ApplicationId,
    status: getStatusOfMember(summary),
    currency: "INR",
    totals: {
      debt: Math.round(debt),
      paid: Math.round(summary.isSettled ? debt : totalEmiPaid),
      remaining,
      overdue,
    },
    sections: [
      {
        title: "Customer",
        items: [
          { label: "Customer Name", value: String(summary.memberName || "").trim() },
          { label: "Address", value: joinAddress(summary) || "-" },
          { label: "Agreement No.", value: summary.ApplicationId },
          { label: "Branch", value: branchName || "-" },
          { label: "CKYC ID", value: maskAadharNumber(summary.aadharNo) || "-" },
        ],
      },
      { title: "Loan", items: loanItems },
      {
        title: "Repayment",
        items: [
          { label: "Instalments Paid", value: `Rs.${formatInr(totalEmiPaid)}` },
          { label: "Principal Paid", value: `Rs.${formatInr(summary.principalPaid || 0)}` },
          { label: "Interest Paid", value: `Rs.${formatInr(summary.interestPaid || 0)}` },
          { label: "Instalment Overdue", value: `Rs.${formatInr(overdue)}` },
          { label: "Future Instalments", value: futureInstalments(summary) },
        ],
      },
    ],
    rows,
    foreclosureSettlement,
    finalBalance: remaining,
  };
};
